// src/api/api.ts
import axios, { AxiosResponse } from 'axios';

const DEFAULT_BASE =
    typeof window !== 'undefined' && window.location.hostname === 'localhost'
        ? 'http://localhost:5189/api'
        : `${window.location.origin}/api`;

const BASE = process.env.REACT_APP_API_BASE ?? DEFAULT_BASE;
const instance = axios.create();

// Track 401 notifications to prevent duplicates
let has401BeenShown = false;
let lastPathname = '';

// Reset the 401 flag when navigating to a new page
const checkAndResetPath = () => {
    if (typeof window !== 'undefined' && window.location.pathname !== lastPathname) {
        lastPathname = window.location.pathname;
        has401BeenShown = false;
    }
};

instance.interceptors.request.use(cfg => {
    const t = localStorage.getItem('token');
    if (t) cfg.headers!['Authorization'] = `Bearer ${t}`;
    return cfg;
});

// Response interceptor to handle 401 errors
instance.interceptors.response.use(
    response => response,
    error => {
        checkAndResetPath();
        if (error.response?.status === 401 && !has401BeenShown) {
            has401BeenShown = true;
            // Dispatch a custom event that the NotificationContext can listen to
            window.dispatchEvent(new CustomEvent('api-unauthorized', {
                detail: { message: 'Session expired. Please log in again.' }
            }));
            // Trigger a global logout so protected routes redirect
            window.dispatchEvent(new CustomEvent('force-logout'));
        }
        return Promise.reject(error);
    }
);

// Frontend version extraction utility
export class VersionExtractor {
    private static readonly BASE_DOWNLOAD_URL = 'http://zain-technologies.com/';

    public static extractVersionInfo(fileName: string): {
        version: string;
        downloadPath: string;
    } {
        if (!fileName || fileName.trim() === '') {
            throw new Error('File name cannot be null or empty');
        }

        // Remove file extension
        const nameWithoutExtension = this.getFileNameWithoutExtension(fileName);
        
        // Pattern to match version numbers (supports formats like 1.2.3, 1.2, 1.2.3.4, etc.)
        const versionPattern = /\b(\d+(?:\.\d+){1,3})\b/;
        const match = nameWithoutExtension.match(versionPattern);

        if (match) {
            const version = match[1];
            
            // Extract software name (everything before the version number)
            // Generate download path using the exact filename
            const downloadPath = `${this.BASE_DOWNLOAD_URL}${fileName}`;

            return { version, downloadPath };
        }

        // If no version found, treat entire name as software name with version 1.0.0
        const defaultDownloadPath = `${this.BASE_DOWNLOAD_URL}${fileName}`;
        
        return {
            version: '1.0.0',
            downloadPath: defaultDownloadPath
        };
    }

    private static getFileNameWithoutExtension(fileName: string): string {
        const lastDotIndex = fileName.lastIndexOf('.');
        if (lastDotIndex === -1) {
            return fileName;
        }
        return fileName.substring(0, lastDotIndex);
    }
}

// helper that returns Promise<T[]>
async function list<T>(path: string): Promise<T[]> {
    const res: AxiosResponse<T[]> = await instance.get(`${BASE}/${path}`);
    return res.data;
}
async function create<T>(path: string, item: T): Promise<T> {
    const res: AxiosResponse<T> = await instance.post(`${BASE}/${path}`, item);
    return res.data;
}
async function update<T extends { id: number }>(path: string, item: T): Promise<void> {
    await instance.put(`${BASE}/${path}/${item.id}`, item);
}
async function remove(path: string, id: number): Promise<void> {
    await instance.delete(`${BASE}/${path}/${id}`);
}

// Auth
export async function authLogin(email: string, password: string) {
    const res = await instance.post(`${BASE}/auth/login`, { email, password });
    return res.data as { token: string };
}

// Entities
export interface SystemStatusUpload {
    companyName: string; // Required company name for registration
    vatNumber: string; // Required VAT for composite identity
    crnNumber?: string; // Optional
    statusJson: string; // Contains company name, VAT, CRN, and status data
    currentVersion?: string; // Current system version
    machineKey?: string; // Optional after first registration
    encryptedKey?: string; // Optional; server derives if missing
}

export interface SystemStatusUploadResponse {
    message: string;
    systemInfo: {
        isActive: boolean;
        licenseExpiryDate: string;
        systemName: string;
        configuredVersion?: string; // Version configured by admin
    };
}

export interface DeviceStatus {
    id: number;
    machineKey: string; // Actual machine key (decrypted)
    statusJson: string; // Contains all data including company info
    timeStamp: string;
    currentVersion?: string; // Current system version
    domain?: string; // Domain/URL where request originated
}

// Enhanced System Status Interface
export interface EnhancedSystemStatus {
    id: number;
    machineKey: string;
    currentVersion: string;
    timeStamp: string;
    statusData: {
        companyName: string;
        vatNumber: string;
        crnNumber: string;
        systemStatus: {
            cpu: number;
            ram: number;
            disk: number;
            status: string;
            timestamp: string;
        };
    };
    systemInfo: {
        systemName: string;
        hostName?: string;
        description?: string;
        isLicenseActive: boolean;
        licenseExpiryDate: string;
        branch?: {
            name: string;
            location?: string;
            company?: {
                name: string;
                address?: string;
            };
        };
    } | null;
}

// Software Version Management Interfaces
export interface SoftwareVersion {
    id: number;
    fileName: string;
    versionNumber: string;
    downloadPath: string;
    description?: string;
    dateAdded: string;
    fileSizeBytes?: number;
    checksum?: string;
    isActive: boolean;
    releaseType: string;
}

export interface SoftwareVersionRequest {
    fileName: string;
    versionNumber: string;
    downloadPath: string;
    description?: string;
    fileSizeBytes?: number;
    checksum?: string;
    releaseType: string;
}

export interface ExtractVersionResponse {
    version: string;
    downloadPath: string;
}

export interface SoftwareVersionBundle {
    latestVersion: SoftwareVersion | null;
    versions: SoftwareVersion[];
}

// Request interface for generating encryption key with expiry date
export interface GenerateEncryptionKeyRequest {
    expiryDate: string;
}

export interface EncryptionKeyResponse {
    encryptionKey: string;
    displayKey: string;
    generatedAt: string;
    expiryDate?: string;
}

// Request interface for updating system to latest version
export interface UpdateToLatestVersionRequest {
}

// Request interface for updating system to a specific version
export interface UpdateToVersionRequest {
    versionNumber: string;
}

// Response interface for update to latest version
export interface UpdateToLatestVersionResponse {
    message: string;
    systemId: number;
    systemName: string;
    machineKey: string;
    previousVersion: string;
    newConfiguredVersion: string;
    latestVersionInfo: {
        id: number;
        fileName: string;
        versionNumber: string;
        description?: string;
        dateAdded: string;
        downloadPath: string;
    };
}

export interface UpdateToVersionResponse {
    message: string;
    systemId: number;
    systemName: string;
    machineKey: string;
    previousVersion: string;
    newConfiguredVersion: string;
    selectedVersionInfo: {
        id: number;
        fileName: string;
        versionNumber: string;
        description?: string;
        dateAdded: string;
        downloadPath: string;
    };
}

export interface GoogleDriveResolveResponse {
    exists: boolean;
    fileId?: string;
    fileName?: string;
    isZip?: boolean;
    directDownloadUrl?: string;
    fileSizeBytes?: number;
    contentType?: string;
    failureReason?: string;
}

export interface GoogleDriveDownloadToServerResponse {
    message: string;
    fileName: string;
    savedPath: string;
    fileSizeBytes: number;
    overwritten: boolean;
}

export interface GoogleDriveClearDownloadsResponse {
    deletedFiles: number;
    deletedBytes: number;
    folder: string;
}

export const api = {
    auth: {
        login: authLogin
    },
    companies: {
        list: () => list<Company>('companies'),
        create: (c: Company) => create<Company>('companies', c),
        update: (c: Company) => update<Company>('companies', c),
        delete: (id: number) => remove('companies', id),
    },
    branches: {
        list: () => list<Branch>('branches'),
        listByCompany: (companyId: number) => list<Branch>(`branches/company/${companyId}`),
        create: (b: Branch) => create<Branch>('branches', b),
        update: (b: Branch) => update<Branch>('branches', b),
        delete: (id: number) => remove('branches', id),
    },
    resellers: {
        list: () => list<Reseller>('reseller'),
        create: (r: Reseller) => create<Reseller>('reseller', r),
        update: (id: number, r: Reseller) => instance.put(`${BASE}/reseller/${id}`, r),
        delete: (id: number) => remove('reseller', id),
    },
    systems: {
        list: (filters?: {
            resellerId?: number;
            registrationFrom?: string;
            registrationTo?: string;
            expiryFrom?: string;
            expiryTo?: string;
            registrationMethod?: string;
            expiryPreset?: string;
            includeInactive?: boolean;
        }) => {
            const sp = new URLSearchParams();
            if (filters?.resellerId != null) sp.set('resellerId', String(filters.resellerId));
            if (filters?.registrationFrom) sp.set('registrationFrom', filters.registrationFrom);
            if (filters?.registrationTo) sp.set('registrationTo', filters.registrationTo);
            if (filters?.expiryFrom) sp.set('expiryFrom', filters.expiryFrom);
            if (filters?.expiryTo) sp.set('expiryTo', filters.expiryTo);
            if (filters?.registrationMethod) sp.set('registrationMethod', filters.registrationMethod);
            if (filters?.expiryPreset) sp.set('expiryPreset', filters.expiryPreset);
            if (filters?.includeInactive) sp.set('includeInactive', 'true');
            const params = sp.toString();
            return list<DeviceSystem>(`systems${params ? `?${params}` : ''}`);
        },
        listByBranch: (branchId: number) => list<DeviceSystem>(`systems/branches/${branchId}`),
        create: (s: DeviceSystem) => create<DeviceSystem>('systems', s),
        update: (s: DeviceSystem) => update<DeviceSystem>('systems', s),
        delete: (id: number) => remove('systems', id),
        recent: () => list<DeviceSystem>('systems/recent'), // GET /api/systems/recent
        expiring: () => list<DeviceSystem>('systems/expiring'), // GET /api/systems/expiring
        generateEncryptionKey: (id: number, request: GenerateEncryptionKeyRequest) =>
            instance.post<EncryptionKeyResponse>(`${BASE}/systems/${id}/generate-encryption-key`, request).then(res => res.data),
        getEncryptionKey: (id: number) =>
            instance.get<EncryptionKeyResponse>(`${BASE}/systems/${id}/encryption-key`).then(res => res.data),
        clearEncryptionKey: (id: number) =>
            instance.post<{ message: string }>(`${BASE}/systems/${id}/clear-encryption-key`).then(res => res.data),
        generateMachineKey: (id: number) =>
            instance.post<{ machineKey: string; encryptionKey?: string; generatedAt?: string }>(`${BASE}/systems/${id}/generate-machine-key`).then(res => res.data),
        updateToLatestVersion: (id: number) =>
            instance.post(`${BASE}/systems/${id}/update-to-latest-version`).then(res => res.data),
        updateToVersion: (id: number, request: UpdateToVersionRequest) =>
            instance.post<UpdateToVersionResponse>(`${BASE}/systems/${id}/update-to-version`, request).then(res => res.data),
    },
    systemStatus: {
        upload: (status: SystemStatusUpload) =>
            instance.post<SystemStatusUploadResponse>(`${BASE}/systemstatus/upload`, status).then(res => res.data),
        latest: () =>
            instance.get<DeviceStatus[]>(`${BASE}/systemstatus/latest`).then(res => res.data),
        enhanced: () =>
            instance.get<any[]>(`${BASE}/systemstatus/enhanced`).then(res => res.data),
        clearHistory: () =>
            instance.post<{ deleted: number; kept: number }>(`${BASE}/systemstatus/clear-history`).then(res => res.data),
    },
    softwareVersions: {
        list: () => list<SoftwareVersion>(`softwareversion`),
        grouped: () => 
            instance.get<SoftwareVersionBundle>(`${BASE}/softwareversion/grouped`).then(res => res.data),
        get: (id: number) =>
            instance.get<SoftwareVersion>(`${BASE}/softwareversion/${id}`).then(res => res.data),
        create: (request: SoftwareVersionRequest) => 
            instance.post<SoftwareVersion>(`${BASE}/softwareversion`, request).then(res => res.data),
        update: (id: number, request: SoftwareVersionRequest) =>
            instance.put(`${BASE}/softwareversion/${id}`, request),
        delete: (id: number) => remove('softwareversion', id),
        // Remove the extractVersion method as it's now handled on frontend
    },
    googleDrive: {
        resolve: (url: string) =>
            instance.get<GoogleDriveResolveResponse>(`${BASE}/googledrive/resolve`, { params: { url } }).then(res => res.data),
        downloadToServer: (url: string, overwrite = false) =>
            instance.post<GoogleDriveDownloadToServerResponse>(`${BASE}/googledrive/download-to-server`, null, { params: { url, overwrite } }).then(res => res.data),
        clearDownloads: () =>
            instance.post<GoogleDriveClearDownloadsResponse>(`${BASE}/googledrive/clear-downloads`, null).then(res => res.data),
    },
    zatcaXml: {
        list: (machineKey: string, params?: { page?: number; pageSize?: number; sortBy?: string; from?: string; to?: string }) =>
            instance.get<ZatcaXmlListResponse>(`${BASE}/zatcaxml/list/${encodeURIComponent(machineKey)}`, { params }).then(res => res.data),
        downloadUrl: (machineKey: string, recordType: string, recordId: number) =>
            `${BASE}/zatcaxml/download-one/${encodeURIComponent(machineKey)}/${encodeURIComponent(recordType)}/${recordId}`,
        taxSummary: (machineKey: string, from: string, to: string) =>
            instance.get<ZatcaTaxSummaryResponse>(`${BASE}/zatcaxml/tax-summary`, { params: { machineKey, from, to } }).then(res => res.data),
        uploadedMachineKeys: () =>
            instance.get<{ machineKeys: string[] }>(`${BASE}/zatcaxml/uploaded-machine-keys`).then(res => res.data.machineKeys),
    },
};

// and don't forget to export your interfaces from some shared file
export interface Company { id: number; name: string; address: string; contactInfo: string; logoUrl: string; }
export interface Branch { id: number; companyId: number; name: string; location: string; contact: string; company?: Company; }
export interface Reseller {
    id: number;
    name: string;
    contactPerson?: string;
    email?: string;
    phone?: string;
    address?: string;
    isActive: boolean;
    createdAt: string;
}

export interface DeviceSystem {
    id: number;
    branchId: number;
    resellerId?: number;
    machineKey?: string;
    systemName: string;
    hostname: string;
    description: string;
    licenseExpiryDate: string;
    licenseExpiryDateSaudi?: string;
    isLicenseActive: boolean;
    isActive?: boolean;
    deactivatedAt?: string;
    branch?: Branch;
    reseller?: Reseller;
    vatNumber?: string;
    crnNumber?: string;
    companyNameSnapshot?: string;
    companyAddressSnapshot?: string;
    encryptionKey?: string;
    encryptionKeyGeneratedAt?: string;
    encryptionKeyExpiryDate?: string;
    configuredVersion?: string; // Version configured by admin
    isZatcaActivated?: boolean; // ZATCA tax integration status

    registeredAtUtc?: string;
    registeredAtSaudi?: string;
    registrationMethod?: string;
}

export interface ZatcaXmlRecordItem {
    recordType: string;
    recordId: number;
    invoiceNumber?: string;
    saleDate?: string;
    issueDate?: string;
    taxOrderNo?: number;
    uploadedAtUtc: string;
    isVerified: boolean;
    hasIssue: boolean;
    errorMessage?: string;
}

export interface ZatcaXmlListResponse {
    totalCount: number;
    page: number;
    pageSize: number;
    items: ZatcaXmlRecordItem[];
}

export interface ZatcaTaxCategoryBreakdown {
    categoryId: string;
    percent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface ZatcaTaxSummaryResponse {
    machineKey: string;
    from: string;
    to: string;
    salesCount: number;
    salesAmountTotal: number;
    salesTaxableTotal: number;
    salesTaxTotal: number;
    returnsCount: number;
    returnsAmountTotal: number;
    returnsTaxableTotal: number;
    returnsTaxTotal: number;
    netAmountTotal: number;
    netTaxableTotal: number;
    netTaxAmount: number;
    parseErrorCount: number;
    categoryBreakdown: ZatcaTaxCategoryBreakdown[];
}